import { ArrowRight, ShieldCheck } from 'lucide-react';
import {
  OrgAdminEmptyState,
  OrgAdminTableSkeleton,
} from '@/components/molecules/org-admin/OrgAdminStates';
import { ChartCard, ChartValueTable } from '@/components/molecules/org-admin/charts/ChartCard';
import { AdoptionFunnelChart } from '@/components/molecules/org-admin/charts/OverviewCharts';
import type { OrgAdminTab } from '@/components/molecules/org-admin/types';
import type { OrgOverview } from '@/services/org-admin/overview';
import { Section } from '@/components/molecules/org-admin/OrgAdminSection';
import { formatCount } from './overviewLayout';

/**
 * "Who's using nCoder?" — adoption against seats, and the roles that hold them.
 *
 * No longer first: the money band leads now, because the operator who visits weekly needs the bill
 * before the adoption story, and the evaluator who wants the adoption story visits quarterly. "Top
 * members by spend" moved to the money band with the rest of the cost preview — it was a spend
 * ranking sitting under an adoption heading.
 *
 * Every bar here is a real reporting range (7/15/30), not an interpolation: daily actives would need
 * a per-day series the API does not return, and inventing one to fill a familiar funnel shape would
 * make the most reassuring chart on the page the least trustworthy. See `AdoptionBar`.
 */
export interface AdoptionSectionProps {
  data: OrgOverview | null;
  /*
   * Supplied by the panel rather than hard-coded here, because the funnel's buckets are not the
   * same KIND of thing for every period: the quarter nests (this quarter ⊇ this month, so the bars
   * narrow monotonically and the shape means what a funnel's shape implies), while two discrete
   * months sit side by side. One sentence cannot describe both honestly. Same reasoning as
   * `stickinessDescription` on `UsageSection`.
   */
  adoptionDescription: string;
  /** Names the funnel's buckets in the empty state and the sr-only caption. */
  bucketNoun: string;
  width: number | null;
  isLoading: boolean;
  isFetching: boolean;
  onNavigateTab: (tab: OrgAdminTab) => void;
}

export function AdoptionSection({
  data,
  adoptionDescription,
  bucketNoun,
  width,
  isLoading,
  isFetching,
  onNavigateTab,
}: AdoptionSectionProps) {
  return (
    <Section title="Who’s using nCoder?">
      <ChartCard
        title="Adoption"
        description={adoptionDescription}
        isLoading={isLoading}
        isFetching={isFetching}
        isEmpty={!isLoading && (data?.adoption.length ?? 0) <= 1}
        emptyTitle="No activity yet"
        emptyDescription={`No member has run anything in this ${bucketNoun}.`}
        testId="adoption"
        table={
          <ChartValueTable
            caption={`Active members by ${bucketNoun}`}
            columns={[{ label: 'Date range' }, { label: 'Members', align: 'right' }]}
            rows={(data?.adoption ?? []).map((bar) => ({
              key: bar.id,
              cells: [bar.label, formatCount(bar.members)],
            }))}
          />
        }
      >
        <AdoptionFunnelChart bars={data?.adoption ?? []} containerWidth={width} />
      </ChartCard>

      <div className="mt-3">
          {/* Roles are nCoder's grouping of seats — the question "which groups exist and how big
              are they" is the same one, answered by the object this product actually has. */}
          <section className="oa-panel flex min-w-0 flex-col" aria-labelledby="oa-groups-title">
            <header className="oa-toolbar flex items-center justify-between gap-3 py-2.5">
              <h3 id="oa-groups-title" className="oa-title truncate">
                Roles
              </h3>
              <button
                type="button"
                onClick={() => onNavigateTab('people')}
                className="oa-interactive inline-flex h-7 items-center gap-1 rounded-md px-2 text-[11px] font-medium text-muted-foreground hover:bg-muted/50 hover:text-foreground"
              >
                Manage
                <ArrowRight className="h-3 w-3" aria-hidden />
              </button>
            </header>
            {isLoading ? (
              <OrgAdminTableSkeleton rows={3} columns={['70%', '30%']} label="Loading roles" />
            ) : data?.groups.length ? (
              <ChartValueTable
                caption="Roles and how many members hold each"
                columns={[{ label: 'Role' }, { label: 'Members', align: 'right' }]}
                rows={data.groups.map((group) => ({
                  key: group.id,
                  cells: [
                    <>
                      {group.name}
                      {group.isDefault && <span className="oa-meta ml-1.5">· default</span>}
                    </>,
                    group.members,
                  ],
                }))}
              />
            ) : (
              <OrgAdminEmptyState
                icon={ShieldCheck}
                title="No roles yet"
                description="Create a role to group seats by what they are allowed to do."
                className="min-h-[140px]"
              />
            )}
          </section>

      </div>
    </Section>
  );
}
